export function createApiClient(apiBase, botKey) {
    const base = apiBase.replace(/\/$/, '');

    const headers = (extra = {}) => ({
        'X-Bot-Key': botKey,
        Accept: 'application/json',
        ...extra,
    });

    async function parseJson(response) {
        const data = await response.json().catch(() => ({}));

        if (! response.ok) {
            const error = new Error(data.message ?? `HTTP ${response.status}`);
            error.status = response.status;
            error.data = data;
            throw error;
        }

        return data;
    }

    return {
        async fetchConfig() {
            const response = await fetch(`${base}/widget/config/${encodeURIComponent(botKey)}`, {
                method: 'GET',
                headers: headers(),
            });

            return parseJson(response);
        },

        async ensureConversation(sessionId, storedConversationId = null) {
            const response = await fetch(`${base}/widget/conversations`, {
                method: 'POST',
                headers: headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    session_id: sessionId,
                    conversation_id: storedConversationId,
                }),
            });

            return parseJson(response);
        },

        async fetchMessageHistory(conversationId, sessionId) {
            const url = new URL(
                `${base}/widget/conversations/${encodeURIComponent(conversationId)}/messages`,
            );
            url.searchParams.set('session_id', sessionId);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: headers(),
            });

            return parseJson(response);
        },

        async fetchHandoverState(conversationId) {
            const response = await fetch(
                `${base}/widget/conversations/${encodeURIComponent(conversationId)}/handover-state`,
                {
                    method: 'GET',
                    headers: headers(),
                },
            );

            return parseJson(response);
        },

        streamMessage(conversationId, message) {
            const url = new URL(`${base}/chat/${conversationId}/stream`);
            url.searchParams.set('message', message);

            return fetch(url.toString(), {
                method: 'GET',
                headers: headers({ Accept: 'text/event-stream' }),
            });
        },

        streamAdminMessage(conversationId, message, adminSession = null) {
            const requestHeaders = headers({
                Accept: 'text/event-stream',
                'Content-Type': 'application/json',
            });

            if (adminSession?.token) {
                requestHeaders['X-Admin-MCP-Token'] = adminSession.token;
            }

            if (adminSession?.platformUserId) {
                requestHeaders['X-Platform-User-Id'] = adminSession.platformUserId;
            }

            if (adminSession?.platformTenantId) {
                requestHeaders['X-Platform-Tenant-Id'] = adminSession.platformTenantId;
            }

            return fetch(`${base}/admin-chat/${encodeURIComponent(botKey)}/stream`, {
                method: 'POST',
                headers: requestHeaders,
                body: JSON.stringify({
                    conversation_id: conversationId,
                    message,
                }),
            });
        },

        async submitMessageFeedback(conversationId, messageId, sessionId, satisfaction) {
            const response = await fetch(
                `${base}/widget/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/feedback`,
                {
                    method: 'POST',
                    headers: headers({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({
                        session_id: sessionId,
                        satisfaction,
                    }),
                },
            );

            return parseJson(response);
        },
        async submitLeadForm(formId, conversationId, fields, consentGiven, consentTextSnapshot) {
            const response = await fetch(`${base}/lead-forms/${encodeURIComponent(formId)}/submit`, {
                method: 'POST',
                headers: headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    conversation_id: conversationId,
                    fields,
                    consent_given: consentGiven,
                    consent_text_snapshot: consentTextSnapshot,
                }),
            });

            return parseJson(response);
        },
        async submitBookingForm(conversationId, fields) {
            const response = await fetch(`${base}/booking-form/submit`, {
                method: 'POST',
                headers: headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    conversation_id: conversationId,
                    fields,
                }),
            });

            return parseJson(response);
        },

        async mintVoiceToken() {
            const response = await fetch(`${base}/widget/voice/token`, {
                method: 'POST',
                headers: headers({ 'Content-Type': 'application/json' }),
                body: '{}',
            });

            if (response.status === 429) {
                const data = await response.json().catch(() => ({}));
                const error = new Error(data.message ?? 'voice_unavailable');
                error.status = 429;
                error.data = data;
                throw error;
            }

            return parseJson(response);
        },

        async startVoicePipecatSession(conversationId) {
            const response = await fetch(`${base}/widget/voice/session`, {
                method: 'POST',
                headers: headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ conversation_id: conversationId }),
            });

            if (response.status === 429) {
                const data = await response.json().catch(() => ({}));
                const error = new Error(data.message ?? 'voice_unavailable');
                error.status = 429;
                error.data = data;
                throw error;
            }

            if (! response.ok) {
                const data = await response.json().catch(() => ({}));
                const error = new Error(data.message ?? `HTTP ${response.status}`);
                error.status = response.status;
                error.data = data;
                throw error;
            }

            return parseJson(response);
        },

        async endVoicePipecatSession(token) {
            if (! token) {
                return;
            }
            try {
                await fetch(`${base}/widget/voice/session/end`, {
                    method: 'POST',
                    headers: headers({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ token }),
                });
            } catch {
                // ignore — best-effort concurrent slot release
            }
        },

        async transcribeVoiceAudio(audioBlob) {
            const form = new FormData();
            form.append('audio', audioBlob, 'utterance.webm');

            const response = await fetch(`${base}/widget/voice/transcribe`, {
                method: 'POST',
                headers: {
                    'X-Bot-Key': botKey,
                    Accept: 'application/json',
                },
                body: form,
            });

            if (response.status === 429) {
                const data = await response.json().catch(() => ({}));
                const error = new Error('voice_unavailable');
                error.status = 429;
                error.data = data;
                throw error;
            }

            return parseJson(response);
        },

        async speakVoice(text) {
            const response = await fetch(`${base}/widget/voice/speak`, {
                method: 'POST',
                headers: headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ text }),
            });

            if (response.status === 429) {
                const data = await response.json().catch(() => ({}));
                const error = new Error('voice_unavailable');
                error.status = 429;
                error.data = data;
                throw error;
            }

            if (! response.ok) {
                const data = await response.json().catch(() => ({}));
                const error = new Error(data.message ?? `HTTP ${response.status}`);
                error.status = response.status;
                error.data = data;
                throw error;
            }

            return response.blob();
        },

        async sendVoiceTurn(conversationId, transcript, voiceToken, elapsedSeconds = 0) {
            const response = await fetch(`${base}/voice/turn`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...(voiceToken ? { Authorization: `Bearer ${voiceToken}` } : {}),
                },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    transcript,
                    elapsed_seconds: elapsedSeconds,
                }),
            });

            if (response.status === 429) {
                const data = await response.json().catch(() => ({}));
                const error = new Error('voice_unavailable');
                error.status = 429;
                error.data = data;
                throw error;
            }

            return parseJson(response);
        },

        async sendVoiceUtterance(conversationId, audioBlob, voiceToken, elapsedSeconds = 0) {
            const form = new FormData();
            form.append('audio', audioBlob, 'utterance.webm');
            form.append('conversation_id', conversationId);
            form.append('elapsed_seconds', String(elapsedSeconds));
            if (voiceToken) {
                form.append('voice_token', voiceToken);
            }

            const response = await fetch(`${base}/widget/voice/utterance`, {
                method: 'POST',
                headers: {
                    'X-Bot-Key': botKey,
                    Accept: 'application/json',
                    ...(voiceToken ? { Authorization: `Bearer ${voiceToken}` } : {}),
                },
                body: form,
            });

            if (response.status === 429) {
                const data = await response.json().catch(() => ({}));
                const error = new Error('voice_unavailable');
                error.status = 429;
                error.data = data;
                throw error;
            }

            return parseJson(response);
        },
    };
}
